// Every export pairs a light class with its `dark:` counterpart. Build UI by composing these —
// never a light-only color class. Adding a new surface/text/border color? Add a paired export
// here, backed by a token in tailwind.config.js.

// UI REUSABLE CLASSES
export const cardClass = "bg-background dark:bg-background-dark rounded-xl"
export const primaryText = "text-textPrimary dark:text-textPrimary-dark"
export const secondaryText = "text-textSecondary dark:text-textSecondary-dark"
export const tertiaryText = "text-textTertiary dark:text-textTertiary-dark"
export const quaternaryText = "text-textQuaternary dark:text-textQuaternary-dark"
export const textBlue = "text-textBlue dark:text-textBlue-dark"
export const textGreyInfo = "text-textGreyInfo dark:text-textGreyInfo-dark"
export const textDarkGreen = "text-darkGreenColor dark:text-darkGreenColor-dark"
export const darkText = "text-primary dark:text-primary-dark"

export const boldText = "font-bold" // 700
export const semiBoldText = "font-semibold" // 600
export const mediumText = "font-medium" // 500
export const regularText = "font-normal" // 400


export const extraSmallTextSize = "text-xs" //12px
export const smallTextSize = "text-sm" //14px
export const baseTextSize = "text-base" //16px
export const largeTextSize = "text-lg" //18px
export const extraLargeTextSize = "text-xl" //20px
export const xxLargeTextSize = "text-2xl" //24px
export const xxxLargeTextSize = "text-3xl" //30px

export const rounded = "rounded-xl"
export const roundedLG = "rounded-lg"

// Product / dish names in picking flows (linking, relinking, cost-price setup) must never be
// truncated — the distinguishing part is often at the end (vintage year, size, variant). Wrap
// instead, capped at 3 lines so one absurd name can't blow up the row.
export const selectableName = "line-clamp-3 break-words min-w-0"

export const placeholderPrimary = "placeholder-textTertiary dark:placeholder-textTertiary-dark"

// SURFACES
export const canvasBg = "bg-backgroundDarker dark:bg-backgroundDarker-dark"
export const cardBg = "bg-background dark:bg-background-dark"
export const elevatedBg = "bg-backgroundSecondary dark:bg-backgroundSecondary-dark"

// UI REUSABLE COLORS
export const lightGreenBG = "bg-cardLightGreen dark:bg-cardLightGreen-dark"
export const whiteBG = "bg-background dark:bg-background-dark"
export const cardBackground = "bg-background dark:bg-background-dark"
export const lightBlueBG = "bg-lightBlue dark:bg-lightBlue-dark"
export const lightAmberBG = "bg-amber-50 dark:bg-amber-900/20"
export const lightRedBG = "bg-red-50 dark:bg-red-900/20"
export const darkerBG = "bg-backgroundDarker dark:bg-backgroundDarker-dark"

export const greyTextColor = "text-textGrey dark:text-textGrey-dark"
export const mutedText = "text-muted dark:text-muted-dark"

export const borderPrimary = "border-borderPrimary dark:border-borderPrimary-dark"
export const borderGrey = "border-borderGrey dark:border-borderGrey-dark"
export const borderLight = "border-borderLightGrey dark:border-borderLightGrey-dark"

export const greenTextColor = "text-textGreen dark:text-textGreen-dark"
export const redTextColor = "text-textRed dark:text-textRed-dark"

export const graphPurpleColor = '#7047EB'; //"#069A6E";//
export const graphOrangeColor = '#FFBD4C'; //"#f77c7c";//
export const darkGreenColor = '#0E8965';
export const greenHoverColor = '#0e8964e8';
export const greyInfoColor = '#858D9D';

export const darkGreenTextColor = "text-darkGreenColor dark:text-darkGreenColor-dark"
export const darkOrangeTextColor = "text-darkOrangeColor dark:text-darkOrangeColor-dark"

// ICON ACCENT COLORS — for icon `color`/`stroke` props (JS props, not className), which
// CSS `dark:` can't reach. Pick the right value with `useAppSettingsStore(s => s.darkMode)`.
export const iconGreenAccent = { light: '#0E8965', dark: '#34D399' }; // matches darkGreenColor token
export const iconRedAccent = { light: '#D92D20', dark: '#FCA5A5' }; // matches textRed-dark token

// STAT CARD ICON-TILE TINTS — hex+alpha backgrounds for the 42×42 icon tile in StatCard.
export const iconBgGreen = '#0794551A';
export const iconBgRed = '#D92D201A';
export const iconBgPurple = '#6938EF1A';
export const iconBgBlue = '#2E90FA1A';
export const iconBgAmber = '#F59E0B1A';


// BUTTON CLASSES
// Compact outlined actions shared by inventory and cost-price review.
export const outlinedActionButtonClass = `inline-flex items-center gap-1 px-[7px] py-[5px] text-[14px] font-medium leading-[20px] rounded-md border ${borderPrimary} ${greenTextColor} dark:bg-transparent`
export const greenButtonClass = "rounded-lg px-6 py-2 border border-buttonBorderGreen dark:border-buttonBorderGreen-dark bg-buttonGreenGradient text-white dark:bg-buttonGreenGradient-dark dark:text-white hover:opacity-90 transition flex items-center"
export const greyButtonClass = "rounded-lg px-6 py-2 border border-buttonBorderGrey dark:border-buttonBorderGrey-dark bg-buttonGreyGradient text-white dark:bg-buttonGreyGradient-dark dark:text-white hover:opacity-90 transition flex items-center"
export const disabledButtonClass = "rounded-lg px-6 py-2 border border-buttonBorderDisabled dark:border-buttonBorderDisabled-dark bg-buttonDisabledGradient text-white dark:bg-buttonDisabledGradient-dark dark:text-white cursor-not-allowed transition flex items-center"
export const greenColorClass = "rounded-lg px-2 py-2 bg-darkGreenColor text-white dark:bg-darkGreenColor-dark dark:text-white transition flex items-center hover:bg-greenHoverColor"
export const cancelColorClass = "rounded-lg px-2 py-2 bg-transparent text-secondary dark:bg-darkGreenColor-dark border border-borderGrey dark:border-borderGrey-dark dark:text-white transition flex items-center hover:bg-gray-50"


// BACKGROUND CLASSES
export const whiteBackgroundClass = "bg-background dark:bg-background-dark rounded-xl"
export const productCardClass = "bg-backgroundSecondary dark:bg-backgroundSecondary-dark rounded-2xl"
export const editOptionProductCardClass = "bg-background dark:bg-background-dark rounded-2xl"


// OVERLAY LAYERS
// A slide-over that portals to `document.body` leaves its host's stacking context
// behind, so it competes in the ROOT context — where the hosts that opened it sit at
// z-30 (plain page editor), z-50 (drawers, ProductSetupModal) or z-[60] (an editor a
// drawer deliberately raised above itself). A portalled child panel is by construction
// the newest layer, so it renders above all of them; ties between two such children
// resolve by DOM order, and the newest portal is appended last.
export const overlayTopLayer = "z-[70]"

// RESPONSIVE CLASSES
export const cardResponsiveWidth = "w-[360px] min-[450px]:w-[400px] min-[550px]:w-[450px] min-[1265px]:w-[360px] min-[1350px]:w-[390px] min-[1400px]:w-[420px] min-[1450px]:w-[450px]"

// RANK BADGE — circular numbered badge used in ranked lists (revenue items, restaurant overview).
// Ranks 1/2/3 get distinct colors; rank 4+ all share the grey gradient.
export const getRankBadgeStyle = (rank: number): { background: string } => {
  if (rank === 1) return { background: 'linear-gradient(180deg, #0E8965 0%, #4AC4A0 100%)' };
  if (rank === 2) return { background: 'linear-gradient(180deg, #7F56D9 0%, #9F73FF 100%)' };
  if (rank === 3) return { background: 'linear-gradient(180deg, #F4B857 0%, #F48357 100%)' };
  return { background: 'linear-gradient(180deg, #8C97A6 0%, #818A97 100%)' };
};
